import { Box, Spinner } from '@arpeggi/ui'
import { observer } from 'mobx-react'
import { FC, Suspense } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useNavigate } from 'react-router-dom'
import { styled } from '../../../../lib/stitches'
import { useSamplePacks } from '../../../../queries/samplePacks'
import CenteredLoadingAnimation from '../../../core/CenteredLoadingAnimation'

import SuspenseImage from '../../../core/SuspenseImage'
import Text from '../../../core/Text/Text'
import { FavoriteDisplay } from '../../../profile/SamplePackHeader'
import { useSearch } from '../hooks/useSearch'
import { Loader } from './Table'

const SamplePackItem = styled(Box, {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '15px',
  maxWidth: '170px',
  '@mobile': {
    width: '220px',
    maxWidth: '220px',
    padding: '20px'
  },
  '> img': {
    borderRadius: '6px',
    marginBottom: '15px'
  }
})

const PlayIconWrapper = styled('div', {
  position: 'absolute',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#000',
  left: '20px',
  top: '20px',
  height: '180px',
  width: '180px',
  borderRadius: '10px',
  opacity: 0,
  flexDirection: 'column',
  '@mobile': {
    display: 'flex',
    width: '50%',
    '&:hover': {
      cursor: 'pointer',
      width: 'calc(100% - 40px)',
      opacity: 0.2
    }
  }
})

const SamplePacksTable: FC = observer(() => {
  const navigate = useNavigate()
  const [search] = useSearch()
  const samplePacksQuery = useSamplePacks({
    params: {
      perPage: 50,
      order: 'desc',
      orderBy: 'createdAt',
      search
    }
  })

  const flattenData = samplePacksQuery.data?.pages.flatMap((page) => page.data) || []

  return (
    <Box css={{ display: 'flex', alignItems: 'stretch', minHeight: '100vh' }}>
      <Box css={{ flex: 1, position: 'relative', padding: '30px 20px' }}>
        <Text size={24} fontWeight="semibold" css={{ marginLeft: '20px', marginBottom: '10px' }}>
          Featured Packs
        </Text>
        {samplePacksQuery.isLoading && (
          <Box alignCenter justifyCenter column>
            <Spinner
              css={{ width: '24px', height: '24px', strokeWidth: '1.5px', marginTop: '24px' }}
            />
          </Box>
        )}
        {!samplePacksQuery.isLoading && (
          <InfiniteScroll
            dataLength={flattenData.length}
            hasMore={samplePacksQuery.hasNextPage}
            loader={<Loader />}
            next={samplePacksQuery.fetchNextPage}
          >
            <Box
              css={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 'unset',
                justifyContent: 'center',
                '@mobile': {
                  justifyContent: 'flex-start',
                  gap: '8px'
                }
              }}
            >
              {flattenData.map((samplePack) => (
                <SamplePackItem key={samplePack.id}>
                  <PlayIconWrapper
                    onClick={() => navigate(`/sample-packs/${samplePack.id}`)}
                  ></PlayIconWrapper>
                  <Suspense fallback={<CenteredLoadingAnimation light />}>
                    <SuspenseImage
                      src={samplePack.coverImage.url}
                      alt={samplePack.coverImage.name}
                      css={{
                        width: '140px',
                        height: '140px',
                        objectFit: 'cover',
                        '@mobile': {
                          height: '180px',
                          maxWidth: '180px',
                          width: '180px',
                          maxHeight: '180px',
                          borderRadius: '10px'
                        }
                      }}
                    />
                  </Suspense>
                  <Text
                    size={18}
                    fontWeight="bold"
                    css={{
                      marginBottom: '5px',
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.7
                      }
                    }}
                    onClick={() => navigate(`/sample-packs/${samplePack.id}`)}
                  >
                    {samplePack.title}
                  </Text>
                  <Box
                    css={{
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Text
                      size={16}
                      fontWeight="medium"
                      onClick={() => navigate(`/profile/${samplePack.user.walletAddress}`)}
                      css={{
                        opacity: 0.5,
                        cursor: 'pointer',
                        '&:hover': {
                          opacity: 0.3
                        }
                      }}
                    >
                      {samplePack.user.displayName}
                    </Text>
                    <FavoriteDisplay
                      favoriteCount={samplePack.soundFavoriteCount}
                      textSize={12}
                      iconSize={15}
                    />
                  </Box>
                </SamplePackItem>
              ))}
            </Box>
          </InfiniteScroll>
        )}
      </Box>
    </Box>
  )
})

export default SamplePacksTable
